import { UpdateTenantModalComponent } from './UpdateTenantModal.types';
import { useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';
import { UpdateTenantDto } from '../../../../../api-client';
import { Box, Button, Flexbox, Modal, TextField, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { useTenantForm } from '../../../hooks/useTenantForm';
import { useEffect } from 'react';
import { InlineHint } from '../../../../../common/components/InlineHint/InlineHint';

export const UpdateTenantModal: UpdateTenantModalComponent = ({
  onClose,
  isOpen,
  updateTenant,
  isUpdating,
  error,
  tenant,
}) => {
  const intl = useIntl();
  const { control, handleSubmit, formState, reset } = useTenantForm(intl);

  const onFormSubmit = (updateTenantDto: UpdateTenantDto) => {
    updateTenant({
      id: tenant.id,
      updateTenantDto,
    });
  };

  useEffect(() => {
    reset({
      name: tenant.name,
      alias: tenant.alias,
      adminUser: tenant.adminUser,
    });
  }, [tenant, reset]);

  return (
    <Modal
      data-testid="update-tenant-modal"
      title={`Update ${tenant.name} tenant`}
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            onClick={handleSubmit(onFormSubmit)}
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 1 ||
              isUpdating
            }
          >
            {isUpdating ? 'Updating ...' : 'Update'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <form>
        <Box mb={2}>
          <Controller
            defaultValue=""
            name="name"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                label="Name"
                name="name"
                type="text"
                fullWidth
                ref={ref}
                onChange={onChange}
                value={value}
                hint="Unique name of the tenant"
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="alias"
            control={control}
            defaultValue=""
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Alias"
                type="text"
                ref={ref}
                name="alias"
                value={value}
                onChange={onChange}
                // @ts-ignore
                hint={
                  <span>
                    Tenant alias must be 2-20 chars long, consisting of letters, digits, hyphens.
                    <br />Must begin with a letter and must not start with `hii`
                  </span>
                }
                trailingIcon={
                  <InlineHint>
                    <Typography variant="body2" weight="bold">
                      Alias
                    </Typography>
                    <Typography variant="caption">
                      The alias is used in the URL for accessing the tenant's applications.
                      <br />
                      Ie: {'<tenant-alias>.hiiretail.com'}
                    </Typography>
                  </InlineHint>
                }
                error={formState.errors?.alias?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="adminUser"
            control={control}
            defaultValue=""
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Administrator email"
                name="adminUser"
                type="text"
                ref={ref}
                placeholder="hiiretail@<tenant-domain-name>"
                value={value}
                // @ts-ignore
                hint={
                  <span>
                    Tenant administrator is a root user.<br/>
                    This user will have access to perform all actions on all IAM resources within the Tenant.
                  </span>
                }
                trailingIcon={
                  <InlineHint>
                    <Typography variant="body2" weight="bold">
                      Tenant admin
                    </Typography>
                    <Typography variant="caption">
                      After the tenant is created and an identity provider is set up the following steps should be taken:
                      <br />
                      1. Create a group and assign it the role <b>"IAM Admin"</b><br />
                      2. Create one or more users<br/>

                      3. Add the users to the newly created group<br />

                      The tenant admin <strong>should no longer be used</strong> to administer the tenant after the completion of these steps.
                    </Typography>
                  </InlineHint>
                }
                onChange={onChange}
                error={formState.errors?.adminUser?.message}
              />
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
